/**
 * NotificationCenter Component - Phase 5 Epic 5.4
 * Comprehensive notification inbox with filtering and management
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, CheckCheck, Trash2, Filter, AlertCircle } from 'lucide-react';
import {
  useNotifications,
  NotificationType,
  NotificationPriority,
  Notification,
} from '@/hooks/useNotifications';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

interface NotificationCenterProps {
  userId?: string;
  className?: string;
  maxHeight?: string;
  showFilters?: boolean;
  onNotificationClick?: (notification: Notification) => void;
}

export function NotificationCenter({
  userId = 'anonymous',
  className,
  maxHeight = '600px',
  showFilters = true,
  onNotificationClick,
}: NotificationCenterProps) {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    dismiss,
    clearError,
  } = useNotifications({ userId, autoFetch: true });

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // Apply filters
  const filteredNotifications = notifications.filter((notification) => {
    if (showUnreadOnly && notification.read) return false;
    return true;
  });

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Call custom handler
    if (onNotificationClick) {
      onNotificationClick(notification);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      try {
        // Check if URL is external
        const url = new URL(notification.actionUrl, window.location.origin);

        // If external URL, use window.location
        if (url.origin !== window.location.origin) {
          window.location.href = notification.actionUrl;
        } else {
          // Internal navigation - use React Router
          const path = url.pathname + url.search + url.hash;
          navigate(path);
        }
      } catch (error) {
        // If URL parsing fails, treat as internal path
        navigate(notification.actionUrl);
      }
    }
  };

  // Handle dismiss
  const handleDismiss = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    await dismiss(notificationId);
  };

  return (
    <div className={cn('w-full bg-[#0C1319] rounded-lg shadow-lg border border-white/[0.06]', className)}>
      {/* Header */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <h3 className="font-semibold text-lg">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                {unreadCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {showFilters && (
              <Button
                variant={showUnreadOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              >
                <Filter className="h-4 w-4 mr-1" />
                {showUnreadOnly ? 'Show All' : 'Unread Only'}
              </Button>
            )}

            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark All Read
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
            <button onClick={clearError} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Notification List */}
      <div style={{ maxHeight, overflowY: 'auto' }}>
        {isLoading ? (
          <div className="p-8 text-center text-white/60">
            Loading notifications...
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="p-8 text-center text-white/60">
            {showUnreadOnly ? 'No unread notifications' : 'No notifications'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
                onDismiss={(e) => handleDismiss(e, notification.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Individual notification item component
interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  onDismiss: (e: React.MouseEvent) => void;
}

function NotificationItem({ notification, onClick, onDismiss }: NotificationItemProps) {
  const priorityColors = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-blue-500',
    low: 'bg-gray-500',
  };

  const typeIcons = {
    agent_execution: '🤖',
    campaign_update: '📢',
    milestone_reached: '🎯',
    mention: '@',
    system_alert: '⚙️',
    error_alert: '⚠️',
    approval_required: '✋',
    recommendation: '💡',
  };

  const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div
      className={cn(
        'p-4 cursor-pointer hover:bg-white/[0.04] transition-colors',
        !notification.read && 'bg-blue-50/50'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Priority indicator */}
        <div
          className={cn(
            'w-1 h-full rounded-full flex-shrink-0 self-stretch',
            priorityColors[notification.priority]
          )}
        />

        {/* Type icon */}
        <div className="text-2xl flex-shrink-0">
          {typeIcons[notification.type] || '📬'}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className={cn('font-semibold', !notification.read && 'font-bold')}>
                {notification.title}
              </h4>
              <p className="text-sm text-white/70 mt-1">{notification.message}</p>
            </div>

            {/* Actions */}
            <button
              onClick={onDismiss}
              className="p-1 hover:bg-white/[0.06] rounded transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs text-white/60">
              {timeAgo(notification.createdAt)}
            </span>

            {notification.priority === 'critical' && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded">
                Critical
              </span>
            )}

            {notification.priority === 'high' && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-orange-100 text-orange-800 rounded">
                High
              </span>
            )}

            {!notification.read && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded">
                New
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Compact notification bell icon with badge (for header)
interface NotificationBellProps {
  userId?: string;
  onClick?: () => void;
  className?: string;
}

export function NotificationBell({
  userId = 'anonymous',
  onClick,
  className,
}: NotificationBellProps) {
  const { unreadCount } = useNotifications({ userId, autoFetch: true });

  return (
    <button
      className={cn('relative', className)}
      onClick={onClick}
      aria-label={`Notifications (${unreadCount} unread)`}
    >
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}
